import { useState, useEffect, useRef } from "react";
import useSWR, { mutate } from "swr";
import { api, downloadFile } from "@/lib/app";
import { Sparkle, Trash, CircleNotch, Warning, FilePdf, FileDoc } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

// minimal markdown renderer (headings, bold, lists, paragraphs)
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let listBuf = [];
  let listType = null;
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        listType === "ol" ? (
          <ol key={`l${out.length}`}>{listBuf}</ol>
        ) : (
          <ul key={`l${out.length}`}>{listBuf}</ul>
        ),
      );
      listBuf = [];
      listType = null;
    }
  };
  const inline = (s) =>
    s
      .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
      .map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (p.startsWith("`") && p.endsWith("`")) return <code key={i}>{p.slice(1, -1)}</code>;
        return p;
      });

  lines.forEach((raw, idx) => {
    const line = raw.replace(/\r$/, "");
    if (/^### (.+)/.test(line)) {
      flushList();
      out.push(<h3 key={idx}>{inline(line.replace(/^### /, ""))}</h3>);
    } else if (/^## (.+)/.test(line)) {
      flushList();
      out.push(<h2 key={idx}>{inline(line.replace(/^## /, ""))}</h2>);
    } else if (/^# (.+)/.test(line)) {
      flushList();
      out.push(<h2 key={idx}>{inline(line.replace(/^# /, ""))}</h2>);
    } else if (/^\d+\.\s+/.test(line)) {
      if (listType !== "ol") flushList();
      listType = "ol";
      listBuf.push(<li key={idx}>{inline(line.replace(/^\d+\.\s+/, ""))}</li>);
    } else if (/^[-*]\s+/.test(line)) {
      if (listType !== "ul") flushList();
      listType = "ul";
      listBuf.push(<li key={idx}>{inline(line.replace(/^[-*]\s+/, ""))}</li>);
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      out.push(<p key={idx}>{inline(line)}</p>);
    }
  });
  flushList();
  return out;
}

export default function AiReport({ storeId, period }) {
  const { data: reports = [] } = useSWR(`/stores/${storeId}/reports`, fetcher);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [style, setStyle] = useState("descriptive"); // descriptive | concise
  const pollingRef = useRef(null);

  const current = selected || reports[0];

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const pollReport = (id) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/reports/${id}`);
        const r = res.data;
        if (r.status === "done") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGenerating(false);
          setSelected(r);
          mutate(`/stores/${storeId}/reports`);
          toast.success("AI správa vygenerovaná");
        } else if (r.status === "error") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGenerating(false);
          setSelected(r);
          mutate(`/stores/${storeId}/reports`);
          toast.error(r.error || "Generovanie zlyhalo");
        }
      } catch (err) {
        // 404 / network — keep polling a couple times
        // eslint-disable-next-line no-console
        console.warn("Poll error", err);
      }
    }, 3000);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/reports", {
        store_id: storeId,
        period_from: period.from,
        period_to: period.to,
        style,
      });
      const id = res.data.id;
      setSelected(res.data);
      mutate(`/stores/${storeId}/reports`);
      toast.info(style === "descriptive" ? "Generujem popisnú správu… potrvá to ~90-120 sekúnd." : "Generujem stručnú správu… potrvá to ~60-90 sekúnd.");
      pollReport(id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generovanie zlyhalo");
      setGenerating(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Zmazať túto správu?")) return;
    await api.delete(`/reports/${id}`);
    if (selected?.id === id) setSelected(null);
    mutate(`/stores/${storeId}/reports`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
      <div className="space-y-3">
        <div className="card-flat p-3 space-y-2">
          <p className="label-eyebrow">Štýl správy</p>
          <div className="grid grid-cols-2 gap-1" role="radiogroup" data-testid="ai-style-toggle">
            <button
              onClick={() => setStyle("descriptive")}
              className={`text-xs py-2 rounded-md transition-colors ${style === "descriptive" ? "bg-[var(--brand)] text-white" : "btn-ghost"}`}
              data-testid="ai-style-descriptive"
            >
              Popisná
            </button>
            <button
              onClick={() => setStyle("concise")}
              className={`text-xs py-2 rounded-md transition-colors ${style === "concise" ? "bg-[var(--brand)] text-white" : "btn-ghost"}`}
              data-testid="ai-style-concise"
            >
              Stručná
            </button>
          </div>
          <p className="text-[10px] text-[var(--text-muted)] leading-snug">
            {style === "descriptive"
              ? "Plynulý manažérsky text s odporúčaniami v plnom jazyku (~1500-2200 slov)."
              : "Stručná analytika s odrážkami a číslami (~800 slov)."}
          </p>
        </div>
        <button onClick={generate} disabled={generating} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="generate-report-button">
          <Sparkle size={16} weight="duotone" /> {generating ? "Generujem…" : "Vygenerovať novú správu"}
        </button>
        <p className="text-xs text-[var(--text-muted)] text-center">Pre obdobie {period.from} → {period.to}. Použije aj všetky poznámky a označené body.</p>

        <div className="card-flat overflow-hidden">
          <div className="px-3 py-2 label-eyebrow border-b border-[var(--border-default)]">História ({reports.length})</div>
          <div className="max-h-[60vh] overflow-y-auto scroll-area">
            {reports.length === 0 && <div className="p-4 text-sm text-[var(--text-muted)]">Zatiaľ žiadna správa.</div>}
            {reports.map((r) => {
              const active = current?.id === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  data-testid={`report-item-${r.id}`}
                  className={`w-full text-left p-3 border-b border-[var(--border-default)] block transition-colors ${
                    active ? "bg-[var(--bg-surface-hover)]" : "hover:bg-[var(--bg-surface-hover)]"
                  }`}
                >
                  <div className="text-xs tabular text-[var(--text-secondary)]">{new Date(r.created_at).toLocaleString("sk-SK")}</div>
                  <div className="text-sm tabular mt-1 flex items-center gap-2">
                    {r.period_from} → {r.period_to}
                    {r.status === "generating" && <CircleNotch size={12} className="animate-spin text-[var(--brand)]" />}
                    {r.status === "error" && <Warning size={12} className="text-[var(--critical)]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card-flat p-6 min-h-[60vh]">
        {!current ? (
          <div className="h-full flex items-center justify-center text-center text-[var(--text-secondary)]">
            <div>
              <Sparkle size={32} className="mx-auto mb-3 text-[var(--brand)]" weight="duotone" />
              <p>Vygeneruj prvú AI správu — analyzujem všetky dáta, poznámky a vrátim odporúčania na úpravu personálu.</p>
            </div>
          </div>
        ) : current.status === "generating" ? (
          <div className="h-full flex items-center justify-center text-center" data-testid="report-generating">
            <div>
              <CircleNotch size={36} className="mx-auto mb-3 text-[var(--brand)] animate-spin" />
              <p className="font-display text-lg font-semibold">Generujem správu…</p>
              <p className="text-sm text-[var(--text-secondary)] mt-1">AI analyzuje dáta, potrvá to približne 60-90 sekúnd.</p>
              <p className="text-xs text-[var(--text-muted)] mt-2 tabular">{current.period_from} → {current.period_to}</p>
            </div>
          </div>
        ) : current.status === "error" ? (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <Warning size={36} className="mx-auto mb-3 text-[var(--critical)]" weight="duotone" />
              <p className="font-display text-lg font-semibold text-[var(--critical)]">Generovanie zlyhalo</p>
              <p className="text-sm text-[var(--text-secondary)] mt-1">{current.error}</p>
              <button onClick={() => remove(current.id)} className="btn-ghost mt-3 text-xs">Odstrániť</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-3 pb-3 border-b border-[var(--border-default)]">
              <div>
                <p className="label-eyebrow">Záverečná správa</p>
                <h3 className="font-display text-xl font-semibold tabular mt-0.5">{current.period_from} → {current.period_to}</h3>
                <p className="text-xs text-[var(--text-muted)] mt-1">{new Date(current.created_at).toLocaleString("sk-SK")}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadFile(`/reports/${current.id}/pdf`, `sprava_${current.period_from}_${current.period_to}.pdf`);
                    } catch (e) {
                      console.error("PDF download failed", e);
                      let msg = "PDF sa nepodarilo stiahnuť";
                      try {
                        // Blob response with JSON error body — decode and show real detail.
                        const text = await e.response?.data?.text?.();
                        if (text) {
                          const parsed = JSON.parse(text);
                          if (parsed?.detail) msg = parsed.detail;
                        }
                      } catch { /* ignore */ }
                      toast.error(msg);
                    }
                  }}
                  className="btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid="download-pdf-button"
                >
                  <FilePdf size={14} /> Stiahnuť PDF
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadFile(`/reports/${current.id}/docx`, `sprava_${current.period_from}_${current.period_to}.docx`);
                    } catch (e) {
                      console.error("DOCX download failed", e);
                      let msg = "Word sa nepodarilo stiahnuť";
                      try {
                        const text = await e.response?.data?.text?.();
                        if (text) {
                          const parsed = JSON.parse(text);
                          if (parsed?.detail) msg = parsed.detail;
                        }
                      } catch { /* ignore */ }
                      toast.error(msg);
                    }
                  }}
                  className="btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid="download-docx-button"
                >
                  <FileDoc size={14} /> Stiahnuť Word
                </button>
                <button onClick={() => remove(current.id)} className="btn-ghost text-xs inline-flex items-center gap-1 text-[var(--critical)]" data-testid="delete-report-button">
                  <Trash size={12} /> Zmazať
                </button>
              </div>
            </div>
            <div className="md-report" data-testid="report-content">{renderMarkdown(current.content)}</div>
          </>
        )}
      </div>
    </div>
  );
}
